/**
 * SandboxConfigList — Overview page component for the Sandbox Configurations resource tab.
 *
 * Shows a list of sandbox configurations with a split-view detail panel.
 */

import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Button,
  Badge,
  Tooltip,
  mergeClasses,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  Add20Regular,
  ArrowClockwise20Regular,
  Delete20Regular,
  Edit20Regular,
  BoxMultiple20Regular,
  CheckmarkCircle16Filled,
  DismissCircle16Filled,
  ArrowSync16Regular,
  Box20Regular,
  Settings20Regular,
  Code20Regular,
} from '@fluentui/react-icons';
import { useSandboxConfigStore } from '@/stores/sandboxConfigStore';
import { useAppStore } from '@/stores/appStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useSandboxConfigs, useRebuildSandboxConfig, useDeleteSandboxConfig } from '@/hooks/useSandboxConfigs';
import { useIsProjectOwner, usePermission } from '@/hooks/useUserPermissions';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/services/apiError';
import type { SandboxConfiguration } from '@/types/sandbox';
import { RepositoryAuthType, SandboxProvisioningState, ResourceTierLabels, ResourceTier } from '@/types/sandbox';

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalXXL,
  },
  split: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  listPanel: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  detailPanel: {
    width: '380px',
    flexShrink: 1,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    minWidth: '200px',
  },

  // Sandbox row
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: '1px solid transparent',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0s',
    },
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2Contrast}`,
  },
  rowIcon: {
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
    fontSize: '18px',
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  rowName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  rowMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  rowStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  statusReady: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusBuilding: {
    color: tokens.colorPaletteBlueForeground2,
  },
  statusFailed: {
    color: tokens.colorPaletteRedForeground1,
  },

  // Detail panel
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  detailTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    flex: 1,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    minWidth: 0,
  },
  detailBody: {
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    flex: 1,
  },
  detailSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalL,
  },
  detailSectionTitle: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: tokens.colorNeutralForeground4,
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  detailKey: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  detailValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    textAlign: 'end',
    wordBreak: 'break-all',
    minWidth: 0,
  },
  detailValueMono: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
  },
  detailDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: `${tokens.spacingVerticalL} 0`,
  },
  repoCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  mutedText: {
    color: tokens.colorNeutralForeground3,
  },
  flexButton: {
    flex: 1,
  },
  repoName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  repoUrl: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  tagRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  detailActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexWrap: 'wrap',
  },

  // Empty state
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    flex: 1,
    color: tokens.colorNeutralForeground3,
    padding: tokens.spacingVerticalXXXL,
  },
  emptyIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground4,
  },
});

// =============================================================================
// HELPERS
// =============================================================================

function getStatusIcon(state?: string) {
  switch (state) {
    case SandboxProvisioningState.Ready:
      return <CheckmarkCircle16Filled />;
    case SandboxProvisioningState.InProgress:
      return <ArrowSync16Regular />;
    case SandboxProvisioningState.Failed:
      return <DismissCircle16Filled />;
    default:
      return null;
  }
}

function getStatusLabel(state?: string): string {
  switch (state) {
    case SandboxProvisioningState.Ready:
      return 'Ready';
    case SandboxProvisioningState.InProgress:
      return 'Building…';
    case SandboxProvisioningState.Failed:
      return 'Failed';
    default:
      return 'Unknown';
  }
}

function getSandboxConfigIcon(sandboxConfig: SandboxConfiguration) {
  const hasRepos = (sandboxConfig.properties.repositorySettings?.repositories?.length ?? 0) > 0;
  if (sandboxConfig.properties.provisioningState === SandboxProvisioningState.Failed) return <DismissCircle16Filled />;
  if (hasRepos) return <Code20Regular />;
  return <Box20Regular />;
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function extractRepoName(url: string): string {
  const parts = url.split('/');
  return parts[parts.length - 1] ?? url;
}

function getSandboxConfigSummary(sandboxConfig: SandboxConfiguration): string {
  const repos = sandboxConfig.properties.repositorySettings?.repositories ?? [];

  if (repos.length > 0) {
    return `${repos.length} repo${repos.length !== 1 ? 's' : ''}`;
  }
  return 'Base image';
}

// =============================================================================
// PROPS
// =============================================================================

interface SandboxConfigListProps {
  onCreateSandboxConfig: () => void;
  onEditSandboxConfig: (sandboxConfig: SandboxConfiguration) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const SandboxConfigList = ({ onCreateSandboxConfig, onEditSandboxConfig }: SandboxConfigListProps) => {
  const styles = useStyles();
  const selectedSandboxConfigName = useSandboxConfigStore((s) => s.selectedSandboxConfigName);
  const selectSandboxConfig = useSandboxConfigStore((s) => s.selectSandboxConfig);
  const projectName = useAppStore((s) => s.selectedProject);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { account } = useAuth();
  const { data: isAdmin, isLoading: isAdminPermissionLoading } = useIsProjectOwner(projectName ?? '');
  const { data: userWithAccess, isLoading: isProjectPermissionsLoading } = usePermission(projectName ?? '', undefined, ['Contributor', 'Author']);

  const { data: sandboxConfigs = [], isLoading: isSandboxConfigsLoading, error: sandboxConfigsError, refetch: refetchSandboxConfigs } = useSandboxConfigs({ poll: true });
  const { mutate: rebuildSandboxConfig, isPending: isRebuilding } = useRebuildSandboxConfig();
  const { mutate: deleteSandboxConfig } = useDeleteSandboxConfig();

  const [deleteConfirmName, setDeleteConfirmName] = useState<string | null>(null);

  // Track previous provisioning states to detect transitions to Failed.
  // Scoped to projectName so project switches don't produce stale comparisons.
  const prevStatesRef = useRef<Record<string, SandboxProvisioningState | undefined>>({});
  const prevProjectRef = useRef<string | undefined>(projectName ?? undefined);
  useEffect(() => {
    // Reset when project changes or list empties
    if (projectName !== prevProjectRef.current || sandboxConfigs.length === 0) {
      prevStatesRef.current = {};
      prevProjectRef.current = projectName ?? undefined;
      if (sandboxConfigs.length === 0) return;
    }
    const prev = prevStatesRef.current;
    for (const sandboxConfig of sandboxConfigs) {
      const prevState = prev[sandboxConfig.name];
      const currentState = sandboxConfig.properties.provisioningState;
      if (prevState && prevState !== SandboxProvisioningState.Failed && currentState === SandboxProvisioningState.Failed) {
        addNotification({
          type: 'error',
          title: 'Sandbox build failed',
          message: `Sandbox failed to provision for sandbox configuration "${sandboxConfig.name}".`,
        });
      }
    }
    prevStatesRef.current = Object.fromEntries(sandboxConfigs.map((s) => [s.name, s.properties.provisioningState]));
  }, [sandboxConfigs, addNotification, projectName]);

  // Auto-select first sandbox config if none selected
  useEffect(() => {
    if (sandboxConfigs.length > 0 && !selectedSandboxConfigName) {
      selectSandboxConfig(sandboxConfigs[0]?.name ?? null);
    }
  }, [sandboxConfigs, selectedSandboxConfigName, selectSandboxConfig]);

  const selectedSandboxConfig = useMemo(
    () => sandboxConfigs.find((s) => s.name === selectedSandboxConfigName) ?? null,
    [sandboxConfigs, selectedSandboxConfigName],
  );
  const userCanEdit = useMemo(() => selectedSandboxConfig?.properties?.createdBy?.objectId?.toLowerCase() === account?.localAccountId?.toLowerCase(), [selectedSandboxConfig, account]);
  const userCanCreate = useMemo(() => isAdmin || userWithAccess, [isAdmin, userWithAccess]);
  const isCreatePermissionsLoading = useMemo(() => isAdminPermissionLoading || isProjectPermissionsLoading, [isAdminPermissionLoading, isProjectPermissionsLoading]);

  const handleRefresh = useCallback(() => {
    void refetchSandboxConfigs();
  }, [refetchSandboxConfigs]);

  const handleDelete = useCallback(
    (name: string) => {
      setDeleteConfirmName(name);
    },
    [],
  );

  const confirmDelete = useCallback(() => {
    if (deleteConfirmName) {
      deleteSandboxConfig(deleteConfirmName, {
        onSuccess: () => {
          if (selectedSandboxConfigName === deleteConfirmName) {
            selectSandboxConfig(null);
          }
        },
      });
      setDeleteConfirmName(null);
    }
  }, [deleteConfirmName, deleteSandboxConfig, selectedSandboxConfigName, selectSandboxConfig]);

  const handleRebuild = useCallback(
    (sandboxConfig: SandboxConfiguration) => {
      if (!projectName) return;
      rebuildSandboxConfig({
        sandboxConfigName: sandboxConfig.name,
        projectName,
      }, {
        onSuccess: () => {
          addNotification({
            type: 'success',
            title: 'Rebuild started',
            message: `Rebuilding sandbox snapshot for sandbox configuration "${sandboxConfig.name}".`,
          });
        },
        onError: (error) => {
          const isConflict = error instanceof ApiError && error.status === 409;
          addNotification({
            type: isConflict ? 'warning' : 'error',
            title: isConflict ? 'Build already in progress' : 'Rebuild failed to start',
            message: isConflict
              ? `A build is already in progress for sandbox configuration "${sandboxConfig.name}". Please wait for it to complete.`
              : `Failed to start sandbox snapshot rebuild for sandbox configuration "${sandboxConfig.name}": ${error.message}`,
          });
        },
      });
    },
    [rebuildSandboxConfig, addNotification, projectName],
  );



  if (isSandboxConfigsLoading && sandboxConfigs.length === 0) {
    return (
      <div className={styles.empty}>
        <Spinner size="medium" label="Loading sandbox configurations..." />
      </div>
    );
  }

  // Show error with retry option when fetch fails (even if sandbox configurations is empty)
  if (sandboxConfigsError && sandboxConfigs.length === 0) {
    return (
      <div className={styles.empty}>
        <BoxMultiple20Regular className={styles.emptyIcon} />
        <Text size={400} weight="semibold">
          Failed to load sandbox configurations
        </Text>
        <Text>{sandboxConfigsError.message ?? 'An error occurred'}</Text>
        <Button appearance="primary" onClick={() => refetchSandboxConfigs()}>
          Retry
        </Button>
      </div>
    );
  }

  if (sandboxConfigs.length === 0) {
    return (
      <div className={styles.empty}>
        <BoxMultiple20Regular className={styles.emptyIcon} />
        <Text size={400} weight="semibold">
          No sandbox configurations
        </Text>
        {userCanCreate && !isCreatePermissionsLoading && (
          <>
            <Text>Create a sandbox configuration to give agents isolated environments for code execution</Text>
            <Button appearance="primary" icon={<Add20Regular />} disabled={isCreatePermissionsLoading} onClick={onCreateSandboxConfig} aria-label="Create sandbox">
              Create
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Error banner for fetch errors only — mutation errors surface via notifications */}
      {sandboxConfigsError && (
        <MessageBar intent="error">
          <MessageBarBody>{sandboxConfigsError.message ?? 'An error occurred'}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.toolbar}>
        <Tooltip content="Refresh" relationship="label">
          <Button
            appearance="subtle"
            icon={<ArrowClockwise20Regular />}
            onClick={handleRefresh}
            aria-label="Refresh sandbox configurations"
          />
        </Tooltip>
        {userCanCreate && !isCreatePermissionsLoading && (
          <Button appearance="primary" icon={<Add20Regular />} disabled={isCreatePermissionsLoading} onClick={onCreateSandboxConfig} aria-label="Create sandbox">
            Create
          </Button>
        )}
      </div>

      <div className={styles.split}>
        {/* List panel */}
        <div className={styles.listPanel} role="listbox" aria-label="Sandbox configurations">
          {sandboxConfigs.map((sandboxConfig) => {
            const isSelected = sandboxConfig.name === selectedSandboxConfigName;
            const state = sandboxConfig.properties.provisioningState;
            const statusStyle =
              state === SandboxProvisioningState.Ready
                ? styles.statusReady
                : state === SandboxProvisioningState.InProgress
                  ? styles.statusBuilding
                  : styles.statusFailed;

            return (
              <div
                key={sandboxConfig.name}
                className={mergeClasses(styles.row, isSelected && styles.rowSelected)}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => selectSandboxConfig(sandboxConfig.name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectSandboxConfig(sandboxConfig.name);
                  }
                }}
              >
                <div className={styles.rowIcon}>{getSandboxConfigIcon(sandboxConfig)}</div>
                <div className={styles.rowContent}>
                  <Text className={styles.rowName}>{sandboxConfig.name}</Text>
                  <Text className={styles.rowMeta}>{getSandboxConfigSummary(sandboxConfig)}</Text>
                </div>
                <div className={mergeClasses(styles.rowStatus, statusStyle)}>
                  {getStatusIcon(state)}
                  <Text size={200} weight="medium">
                    {getStatusLabel(state)}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        {selectedSandboxConfig && (
          <div className={styles.detailPanel}>
            <div className={styles.detailHeader}>
              <Settings20Regular />
              <Text className={styles.detailTitle}>{selectedSandboxConfig.name}</Text>
            </div>

            <div className={styles.detailBody}>
              {/* Status section */}
              <div className={styles.detailSection}>
                <Text className={styles.detailSectionTitle}>Status</Text>
                <div className={styles.detailRow}>
                  <Text className={styles.detailKey}>State</Text>
                  <Text
                    className={mergeClasses(
                      styles.detailValue,
                      selectedSandboxConfig.properties.provisioningState === SandboxProvisioningState.Ready
                        ? styles.statusReady
                        : selectedSandboxConfig.properties.provisioningState === SandboxProvisioningState.Failed
                          ? styles.statusFailed
                          : styles.statusBuilding,
                    )}
                  >
                    ● {getStatusLabel(selectedSandboxConfig.properties.provisioningState)}
                  </Text>
                </div>
                {selectedSandboxConfig.properties.createdTime && (
                  <div className={styles.detailRow}>
                    <Text className={styles.detailKey}>Created</Text>
                    <Text className={styles.detailValue}>
                      {formatDate(selectedSandboxConfig.properties.createdTime)}
                    </Text>
                  </div>
                )}
                <div className={styles.detailRow}>
                  <Text className={styles.detailKey}>Resource Tier</Text>
                  <Text className={styles.detailValue}>
                    {ResourceTierLabels[selectedSandboxConfig.properties.resourceSettings?.resourceTier ?? ResourceTier.M] ?? selectedSandboxConfig.properties.resourceSettings?.resourceTier ?? 'M'}
                  </Text>
                </div>
                {selectedSandboxConfig.properties.errorMessage && (
                  <div className={styles.detailRow}>
                    <Text className={styles.detailKey}>Error</Text>
                    <Text className={mergeClasses(styles.detailValue, styles.statusFailed)}>
                      {selectedSandboxConfig.properties.errorMessage}
                    </Text>
                  </div>
                )}
              </div>

              <div className={styles.detailDivider} />

              {/* Repositories section */}
              <div className={styles.detailSection}>
                <Text className={styles.detailSectionTitle}>Repositories</Text>
                {(selectedSandboxConfig.properties.repositorySettings?.repositories ?? []).length === 0 ? (
                  <Text size={200} className={styles.mutedText}>
                    No repositories configured
                  </Text>
                ) : (
                  selectedSandboxConfig.properties.repositorySettings!.repositories.map((repo) => (
                    <div key={repo.url} className={styles.repoCard}>
                      <Text className={styles.repoName}>{extractRepoName(repo.url)}</Text>
                      <Text className={styles.repoUrl}>{repo.url}</Text>
                      <div className={styles.tagRow}>
                        <Badge appearance="tint" color="informative" size="small">
                          {repo.branch}
                        </Badge>
                        <Badge appearance="tint" color="success" size="small">
                          {repo.authType === RepositoryAuthType.OAuth && repo.connectorType
                            ? `OAuth · ${repo.connectorType === 'azuredevops' ? 'Azure DevOps' : 'GitHub'}`
                            : repo.authType}
                        </Badge>
                        {repo.authType === RepositoryAuthType.OAuth && repo.connectionId && (
                          <Badge appearance="tint" color="brand" size="small">
                            {repo.connectionId.includes('/') ? repo.connectionId.split('/').pop() : repo.connectionId}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Actions */}
            {userCanEdit && <div className={styles.detailActions}>
              <Button
                appearance="secondary"
                icon={<Edit20Regular />}
                className={styles.flexButton}
                onClick={() => onEditSandboxConfig(selectedSandboxConfig)}
              >
                Edit
              </Button>
              <Button
                appearance="secondary"
                icon={<ArrowClockwise20Regular />}
                className={styles.flexButton}
                disabled={isRebuilding || selectedSandboxConfig.properties.provisioningState === SandboxProvisioningState.InProgress}
                onClick={() => handleRebuild(selectedSandboxConfig)}
              >
                Rebuild
              </Button>
              <Tooltip content="Delete sandbox configuration" relationship="label">
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  onClick={() => handleDelete(selectedSandboxConfig.name)}
                  aria-label={`Delete sandbox configuration "${selectedSandboxConfig.name}"`}
                />
              </Tooltip>
            </div>}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmName !== null}
        onOpenChange={(_, data) => { if (!data.open) setDeleteConfirmName(null); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete sandbox configuration</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{deleteConfirmName}</strong>?
              This action cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteConfirmName(null)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

SandboxConfigList.displayName = 'SandboxConfigList';
